import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  InspectStats,
  ClipHealth,
  fetchInspectStats,
  fetchInspectClips,
  fetchDaemonStatus,
  setInspectAdminSecret,
  getInspectAdminSecret
} from '../services/inspectService';
import { InspectNav, InspectViewTab } from '../components/inspect/InspectNav';
import { ClipsTab } from '../components/inspect/ClipsTab';
import { UsersTab } from '../components/inspect/UsersTab';
import { TokenomicsTab } from '../components/inspect/TokenomicsTab';
import { DatabaseTab } from '../components/inspect/DatabaseTab';
import { DaemonMonitorTab } from '../components/inspect/DaemonMonitorTab';
import { StorageTab } from '../components/inspect/StorageTab';
import { DiagnosticsTab } from '../components/inspect/DiagnosticsTab';

export const Inspect: React.FC = () => {
  const [activeTab, setActiveTab] = useState<InspectViewTab>('clips');
  const [stats, setStats] = useState<InspectStats | null>(null);
  const [allClips, setAllClips] = useState<ClipHealth[]>([]);
  const [unbakedCount, setUnbakedCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [accessDenied, setAccessDenied] = useState<boolean>(false);
  const [adminKeyInput, setAdminKeyInput] = useState<string>('');

  // In-flight mutex to prevent duplicate or stacked requests
  const isFetchingRef = useRef(false);

  // Load clips & stats on mount or manual user refresh
  const loadData = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoading(true);
    try {
      const [statsData, clipsData, daemonData] = await Promise.all([
        fetchInspectStats(),
        fetchInspectClips({ includeHealth: false }),
        fetchDaemonStatus()
      ]);
      if (statsData) {
        setStats(statsData);
        setAccessDenied(false);
      } else if (!clipsData.length && !daemonData) {
        // If all requests failed, user lacks admin session or secret
        setAccessDenied(true);
      }
      if (clipsData) setAllClips(clipsData);
      if (daemonData?.queueSummary) {
        setUnbakedCount(daemonData.queueSummary.totalPending);
      }
      setLastUpdated(new Date());
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  // Strict: Single fetch on mount, strictly ZERO background polling loops
  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleBackToApp = () => {
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleUnlockWithKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminKeyInput.trim()) return;
    setInspectAdminSecret(adminKeyInput.trim());
    await loadData();
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, var(--bg-cosmic) 0%, var(--bg-deep) 100%)',
      padding: '2rem 2.5rem'
    }}>
      <div style={{ maxWidth: '1440px', margin: '0 auto' }}>
        {/* Navigation & Tab Ribbon */}
        <InspectNav
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onRefresh={loadData}
          isLoading={isLoading}
          lastUpdated={lastUpdated}
          onBackToApp={handleBackToApp}
          unbakedCount={unbakedCount}
          orphanedCount={stats?.orphanedClips || 0}
        />

        {/* View Panes or Access Challenge */}
        {accessDenied && !isLoading ? (
          <div style={{
            background: 'rgba(15, 23, 42, 0.85)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '16px',
            padding: '3rem 2rem',
            textAlign: 'center',
            maxWidth: '540px',
            margin: '4rem auto',
            backdropFilter: 'blur(16px)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.5)'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f8fafc', marginBottom: '0.5rem' }}>
              Administrator Authorization Required
            </h2>
            <p style={{ color: '#94a3b8', fontSize: '0.95rem', lineHeight: '1.5', marginBottom: '2rem' }}>
              Inspector telemetry and database management are restricted. Please sign in with an administrator account or provide the master admin secret key.
            </p>
            <form onSubmit={handleUnlockWithKey} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <input
                type="password"
                placeholder="Enter Master Admin Secret..."
                value={adminKeyInput}
                onChange={(e) => setAdminKeyInput(e.target.value)}
                style={{
                  background: 'rgba(30, 41, 59, 0.9)',
                  border: '1px solid rgba(148, 163, 184, 0.25)',
                  borderRadius: '8px',
                  padding: '0.75rem 1rem',
                  color: '#fff',
                  fontSize: '1rem',
                  outline: 'none',
                  textAlign: 'center'
                }}
              />
              <button
                type="submit"
                style={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.75rem 1.5rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Authenticate Session
              </button>
              <button
                type="button"
                onClick={handleBackToApp}
                style={{
                  background: 'transparent',
                  color: '#64748b',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  marginTop: '0.5rem'
                }}
              >
                ← Return to SoulSync
              </button>
            </form>
          </div>
        ) : (
          <>
            {activeTab === 'clips' && (
              <ClipsTab
                stats={stats}
                allClips={allClips}
                isLoading={isLoading}
                onRefreshData={loadData}
              />
            )}

            {activeTab === 'users' && (
              <UsersTab onRefreshData={loadData} />
            )}

            {activeTab === 'tokenomics' && (
              <TokenomicsTab onRefreshAll={loadData} />
            )}

            {activeTab === 'database' && (
              <DatabaseTab />
            )}

            {activeTab === 'daemon' && (
              <DaemonMonitorTab onRefreshAll={loadData} />
            )}

            {activeTab === 'storage' && (
              <StorageTab />
            )}

            {activeTab === 'diagnostics' && (
              <DiagnosticsTab />
            )}
          </>
        )}
      </div>

      {/* Global slide animation & spin CSS */}
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default Inspect;